import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';

export interface AiThought {
    /** Stable id — pair tool-call/tool-result events to update `ok`. */
    id: string;
    /** Human-readable label, e.g. "Querying violations…". */
    label: string;
    /** Undefined while in-flight; true on success, false on failure. */
    ok?: boolean;
    /**
     * 'tool' — a structural tool-call row (status icon + label).
     * 'summary' — a layperson-safe paraphrase of the model's reasoning,
     *             rendered as italic prose between/above tool rows.
     * Default 'tool' when omitted so existing callsites keep working.
     */
    kind?: 'tool' | 'summary';
}

/**
 * One contiguous block of summary thoughts that the template renders as a
 * single prose paragraph stack, or one tool-call row that gets a status
 * icon. Grouping summaries this way (instead of the historical
 * one-row-per-summary look with a check icon on every line) reads as
 * calm narration rather than a wall of identical checkmark rows.
 */
type ThoughtGroup =
    | { id: string; kind: 'summaries'; items: AiThought[] }
    | { id: string; kind: 'tool'; tool: AiThought; repeatCount: number };

/**
 * Renders a "Thought for Xs" panel: a single collapsible block of model
 * reasoning, modeled on Claude.ai's "Thought process" panel. Summary
 * rows flow as italic prose; tool calls appear as discrete status rows
 * with a spinner / check / X icon.
 *
 * 👋 Heads-up on icons: this component uses the Phosphor webfont
 * (`<i class="ph-bold ph-…">`) instead of the V2 SVG sprite. That's
 * intentional — we're piloting Phosphor here alongside the AI assistant
 * page and approval dialog to evaluate it against the sprite pattern.
 */
@Component({
    selector: 'app-ai-thought-trail',
    templateUrl: './ai-thought-trail.component.html',
    styleUrls: ['./ai-thought-trail.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule],
})
export class AiThoughtTrailComponent {
    private _thoughts: ReadonlyArray<AiThought> = [];
    private _groups: ThoughtGroup[] = [];

    /**
     * Grouped view of the thoughts, recomputed only when the input changes
     * (NOT on every change-detection pass). The template binds `groups`
     * twice, so a getter that rebuilt each read would do the O(n) collapse
     * work twice per CD tick — memoizing on set keeps it to once per update.
     */
    @Input()
    set thoughts(value: ReadonlyArray<AiThought>) {
        this._thoughts = value ?? [];
        this._groups = this._buildGroups(this._thoughts);
    }
    get thoughts(): ReadonlyArray<AiThought> {
        return this._thoughts;
    }
    /** Streamed extended-thinking prose. Retained for backwards-compat;
     *  the current backend never emits raw thinking, so it goes unused. */
    @Input() thinkingContent: string | null = null;
    /** True while the assistant turn is still streaming. Drives the header label. */
    @Input() streaming = false;
    /** Final elapsed seconds — only shown once `streaming` flips to false. */
    @Input() elapsedSeconds: number | null = null;
    @Input() collapsed = false;

    @Output() collapsedChange = new EventEmitter<boolean>();

    /**
     * Collapse consecutive summary rows into a single prose group so the
     * panel reads as paragraphs of narration. Tool rows remain discrete.
     *
     * Drops a trailing colon from each summary label — Haiku occasionally
     * ends a phase summary with `:` (when it slips toward "...to do X:"
     * phrasing). Periods read better in the rendered prose.
     */
    get groups(): ThoughtGroup[] {
        return this._groups;
    }

    /**
     * Collapse consecutive summary rows into a single prose group so the
     * panel reads as paragraphs of narration. Tool rows remain discrete.
     *
     * Drops a trailing colon from each summary label — Haiku occasionally
     * ends a phase summary with `:` (when it slips toward "...to do X:"
     * phrasing). Periods read better in the rendered prose.
     *
     * Built immutably (new array per step) so it stays OnPush-friendly and
     * free of in-place mutation.
     */
    private _buildGroups(thoughts: ReadonlyArray<AiThought>): ThoughtGroup[] {
        // Build in a single pass, O(N). Appends/merges use index assignment
        // (`out[out.length] = …`) rather than spreading a new array each step
        // (which was O(N²)) — and rather than `.push`, which the OnPush lint
        // flags even on a method-local array. Each element is a fresh object,
        // so the result stays immutable-by-element / OnPush-friendly.
        const out: ThoughtGroup[] = [];
        let inSummaryBucket = false;

        for (const t of thoughts) {
            const kind = t.kind ?? 'tool';
            if (kind === 'summary') {
                const cleaned = { ...t, label: this._cleanLabel(t.label) };
                const last = out[out.length - 1];
                if (inSummaryBucket && last && last.kind === 'summaries') {
                    out[out.length - 1] = { ...last, items: [...last.items, cleaned] };
                } else {
                    out[out.length] = { id: 'sum:' + t.id, kind: 'summaries', items: [cleaned] };
                    inSummaryBucket = true;
                }
            } else {
                inSummaryBucket = false;
                // Collapse consecutive identical tool labels. When the model
                // fires (say) execute_sql against v_charges six times in a
                // row, the trail becomes "Looking at the charges… × 6" not
                // six identical rows — far less visual noise, and the count
                // is itself a useful signal that the model is thrashing.
                // Status preference is X (failure) > spinner (in-flight) >
                // check (success): a single failure in the group should
                // bubble up, and we'd rather show "still running" than a
                // green check while one of the merged calls is pending.
                const last = out[out.length - 1];
                if (last && last.kind === 'tool' && last.tool.label === t.label) {
                    const merged = this._mergeToolStatus(last.tool, t);
                    out[out.length - 1] = {
                        ...last,
                        repeatCount: last.repeatCount + 1,
                        tool: { ...last.tool, ok: merged },
                    };
                } else {
                    out[out.length] = { id: 'tool:' + t.id, kind: 'tool', tool: t, repeatCount: 1 };
                }
            }
        }
        return out;
    }

    /** X > pending > check. See `groups` for rationale. */
    private _mergeToolStatus(a: AiThought, b: AiThought): boolean | undefined {
        if (a.ok === false || b.ok === false) {
            return false;
        }
        if (a.ok === undefined || b.ok === undefined) {
            return undefined;
        }
        return true;
    }

    private _cleanLabel(label: string): string {
        // Strip trailing punctuation — the template appends a literal "…" to
        // every thought line, so labels must be bare (no ".…" / ":…").
        return (label ?? '').trim().replace(/[.:;,…]+$/, '');
    }

    onToggle(): void {
        // Don't mutate `this.collapsed` locally — emit the new value and let
        // the parent's two-way binding flow it back. Avoids the input/output
        // double-write and keeps state ownership in the parent.
        this.collapsedChange.emit(!this.collapsed);
    }

    trackById(_idx: number, t: AiThought): string {
        return t.id;
    }

    trackByGroup(_idx: number, g: ThoughtGroup): string {
        return g.id;
    }
}
